import { useMemo } from "react";

import { addDays } from "date-fns";
import { minBy } from "lodash-es";

import { ActivityTypeCadenceInfo } from "@mapmycustomers/shared/types/base/Cadenced";
import { Group } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import FrequencyCriteria from "@app/types/frequncy/FrequencyCriteria";
import loggingService from "@app/util/logging";

import FrequencyEntity from "../../type/FrequencyEntity";
import FrequencyPreviewConfig from "../../type/FrequencyPreviewConfig";
import getFrequencySequence from "../../util/getFrequencySequence";
import hasFrequency from "../../util/hasFrequency";

export const getFrequencyPreviewConfig = (
  entity: FrequencyEntity,
  isGroup?: boolean,
  groups?: Group[]
): FrequencyPreviewConfig => {
  const groupsWithFrequencyConfig = groups?.filter(hasFrequency);
  const sequence = getFrequencySequence(entity, groupsWithFrequencyConfig);

  const minimumCadenceInterval = sequence.reduce(
    (min, { cadenceInterval }) => Math.min(min, cadenceInterval),
    Number.MAX_SAFE_INTEGER
  );

  const { cadenceDaysOut, cadenceInterval, crmActivityTypeId } = sequence.reduce(
    (record, candidate) => (record.cadenceDaysOut > candidate.cadenceDaysOut ? candidate : record),
    {
      cadenceDaysOut: Number.MAX_SAFE_INTEGER,
      cadenceInterval: 0,
    }
  );

  let actualFrequencyCriteria: FrequencyCriteria = {
    activityTypesIds: crmActivityTypeId ?? [],
    cadenceInterval,
    dueDate: cadenceDaysOut > 0 ? new Date() : addDays(new Date(), -cadenceDaysOut),
  };

  // the entry with the minimum cadenceDaysOut is the one driving the record's current status
  const drivingCadence = minBy(entity.actTypeCadence, ({ cadenceDaysOut }) => cadenceDaysOut);
  if (drivingCadence?.cadenceInterval) {
    actualFrequencyCriteria = {
      activityTypesIds: drivingCadence.crmActivityTypeId
        ? [drivingCadence.crmActivityTypeId]
        : undefined,
      cadenceInterval: drivingCadence.cadenceInterval,
      dueDate:
        drivingCadence.cadenceDaysOut > 0
          ? new Date()
          : addDays(new Date(), -drivingCadence.cadenceDaysOut),
    };
  }

  const isEntityFrequencyConfigured = hasFrequency(entity);

  // we know there must be a group-defined cadence if any of these
  // are defined
  const isGroupFrequencyConfigured =
    !!groupsWithFrequencyConfig?.length || isDefined(entity.cadenceGroupAccess);

  const typeIds = sequence.reduce<ActivityType["id"][] | undefined>(
    (result, row) =>
      isDefined(result) && isDefined(row.crmActivityTypeId)
        ? [...result, ...(row.crmActivityTypeId ?? [])]
        : undefined,
    []
  );

  const activityTypeIds = typeIds ? Array.from(new Set(typeIds)) : undefined;

  // cadenceGroupAccess === false means the record's current cadence is driven by a group
  // the user cannot see. The record's cadenceDaysOut is the minimum across actTypeCadence
  // entries, so the entry equal to it is the one driving the current status. Fall back to
  // the minimum entry when none matches exactly — that only happens when the platform's
  // min-invariant broke (e.g. data drift between cadence cron runs), hence the warning.
  let cadenceFromInaccessibleGroup: ActivityTypeCadenceInfo | undefined;
  if (entity.cadenceGroupAccess === false) {
    cadenceFromInaccessibleGroup = entity.actTypeCadence?.find(
      (cadence) => cadence.cadenceDaysOut === entity.cadenceDaysOut
    );
    if (!cadenceFromInaccessibleGroup && entity.actTypeCadence?.length) {
      loggingService.warning("Record cadenceDaysOut matches no actTypeCadence entry", {
        cadenceDaysOut: entity.cadenceDaysOut,
        entityId: entity.id,
      });
      cadenceFromInaccessibleGroup = drivingCadence;
    }
  }

  return {
    activityTypeIds,
    actualFrequencyCriteria,
    cadenceFromInaccessibleGroup,
    entity,
    groupsWithFrequencyConfig,
    isEntityFrequencyConfigured,
    isGroup,
    isGroupFrequencyConfigured,
    minimumCadenceInterval,
  };
};

const useFrequencyPreviewConfig = (
  entity: FrequencyEntity,
  isGroup?: boolean,
  initialGroups?: Group[]
) =>
  useMemo(
    () => getFrequencyPreviewConfig(entity, isGroup, initialGroups),
    [entity, isGroup, initialGroups]
  );

export default useFrequencyPreviewConfig;
